<template>
  <div class="air__utils__content">
    <div class="air__utils__heading">
      <div class="row align-items-center">
        <div class="col-md-6 col-12 mb-2 mb-md-0">
          <h5 class="mb-0 font-weight-bold">
            <i class="fe fe-video mr-2 text-primary"></i> Zoom Integration Settings
          </h5>
          <small class="text-muted">Configure your school's Zoom Server-to-Server OAuth and Meeting SDK credentials</small>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Left Column: Connection Status & Form -->
      <div class="col-lg-7 col-12 mb-4">
        <a-card title="Zoom API & SDK Configuration" :bordered="false" class="shadow-sm">
          <a-spin :spinning="loading">
            <!-- Connected Banner -->
            <div v-if="status && status.is_connected" class="mb-4">
              <a-alert
                type="success"
                showIcon
                message="Connected & Verified"
                :description="'Zoom Server-to-Server OAuth is active for Account ID: ' + status.account_id"
              />
              <div class="mt-3 p-3 bg-light rounded text-muted font-size-13">
                <div><strong>Connected On:</strong> {{ status.connected_at || 'Active' }}</div>
                <div><strong>Current Plan:</strong> {{ formatPlan(status.plan_type) }}</div>
                <div><strong>Meeting SDK Key:</strong> {{ status.sdk_key ? status.sdk_key.substring(0, 8) + '...' : 'Not configured' }}</div>
              </div>
            </div>

            <!-- Not Connected Banner -->
            <div v-else class="mb-4">
              <a-alert
                type="warning"
                showIcon
                message="Zoom Not Connected"
                description="Enter your Zoom Server-to-Server OAuth credentials and Meeting SDK keys to enable live online classes."
              />
            </div>

            <!-- Credentials Form -->
            <a-form :form="form" layout="vertical" @submit="handleSubmit">
              <h6 class="font-weight-bold text-dark border-bottom pb-2 mb-3">1. Server-to-Server OAuth (Backend API)</h6>
              <div class="row">
                <div class="col-md-6 col-12">
                  <a-form-item label="Account ID" extra="Found in Zoom App Marketplace > App Credentials">
                    <a-input
                      v-decorator="['account_id', { rules: [{ required: true, message: 'Please enter Zoom Account ID' }] }]"
                      placeholder="e.g. abCdeFGhijkLm123"
                    />
                  </a-form-item>
                </div>
                <div class="col-md-6 col-12">
                  <a-form-item label="Client ID" extra="Server-to-Server OAuth Client ID">
                    <a-input
                      v-decorator="['client_id', { rules: [{ required: true, message: 'Please enter Zoom Client ID' }] }]"
                      placeholder="e.g. xYZ123abc456"
                    />
                  </a-form-item>
                </div>
                <div class="col-12">
                  <a-form-item label="Client Secret" extra="Server-to-Server OAuth Client Secret (stored securely)">
                    <a-input-password
                      v-decorator="['client_secret', { rules: [{ required: !status || !status.has_client_secret, message: 'Please enter Zoom Client Secret' }] }]"
                      :placeholder="status && status.has_client_secret ? '•••••••••••••••• (Leave blank to keep existing)' : 'Enter Client Secret'"
                    />
                  </a-form-item>
                </div>
              </div>

              <h6 class="font-weight-bold text-dark border-bottom pb-2 mb-3 mt-4">2. Meeting SDK Credentials (Mobile In-App Video)</h6>
              <div class="row">
                <div class="col-md-6 col-12">
                  <a-form-item label="Meeting SDK Key" extra="Found in Zoom Marketplace > Meeting SDK App">
                    <a-input
                      v-decorator="['sdk_key', { rules: [{ required: false }] }]"
                      placeholder="e.g. AbcDef12345"
                    />
                  </a-form-item>
                </div>
                <div class="col-md-6 col-12">
                  <a-form-item label="Meeting SDK Secret" extra="Meeting SDK App Secret">
                    <a-input-password
                      v-decorator="['sdk_secret', { rules: [{ required: false }] }]"
                      :placeholder="status && status.has_sdk_secret ? '•••••••••••••••• (Leave blank to keep existing)' : 'Enter SDK Secret'"
                    />
                  </a-form-item>
                </div>
              </div>

              <h6 class="font-weight-bold text-dark border-bottom pb-2 mb-3 mt-4">3. School Zoom Plan Tier</h6>
              <div class="row">
                <div class="col-md-6 col-12">
                  <a-form-item label="Account License Tier">
                    <a-select v-decorator="['plan_type', { initialValue: 'pro' }]">
                      <a-select-option value="pro">Zoom Pro (Up to 100 participants / host)</a-select-option>
                      <a-select-option value="business">Zoom Business (Up to 300 participants / host)</a-select-option>
                      <a-select-option value="education">Zoom Education Campus (Up to 300 participants)</a-select-option>
                      <a-select-option value="education_plus">Zoom Education Plus (Up to 500 participants)</a-select-option>
                    </a-select>
                  </a-form-item>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="mt-4 pt-3 border-top d-flex align-items-center justify-content-between">
                <div>
                  <a-button type="primary" html-type="submit" size="large" :loading="saving" class="mr-2">
                    <i class="fe fe-save mr-1"></i> Save & Verify Settings
                  </a-button>
                  <a-button v-if="status && status.is_connected" size="large" :loading="testing" @click="testConnection">
                    <i class="fe fe-refresh-cw mr-1"></i> Test Connection
                  </a-button>
                </div>

                <a-popconfirm
                  v-if="status && status.is_connected"
                  title="Are you sure you want to disconnect Zoom integration?"
                  okText="Yes, disconnect"
                  cancelText="Cancel"
                  @confirm="disconnect"
                >
                  <a-button type="danger" ghost size="large" :loading="disconnecting">
                    Disconnect
                  </a-button>
                </a-popconfirm>
              </div>
            </a-form>
          </a-spin>
        </a-card>
      </div>

      <!-- Right Column: Setup Instructions -->
      <div class="col-lg-5 col-12">
        <a-card title="How to Setup Zoom for Your School" :bordered="false" class="shadow-sm mb-4">
          <div class="font-size-13 text-muted">
            <h6 class="font-weight-bold text-dark mb-2">Step 1: Create Server-to-Server OAuth App</h6>
            <ol class="pl-3 mb-3">
              <li>Sign in to <a href="https://marketplace.zoom.us/" target="_blank" class="text-primary font-weight-bold">Zoom App Marketplace</a> with your School Admin account.</li>
              <li>Click <strong>Develop > Build App</strong> and select <strong>Server-to-Server OAuth</strong>.</li>
              <li>Copy your <strong>Account ID</strong>, <strong>Client ID</strong>, and <strong>Client Secret</strong>.</li>
              <li>Add the following Scopes:
                <ul class="mt-1">
                  <li><code>meeting:write:admin</code></li>
                  <li><code>meeting:read:admin</code></li>
                  <li><code>user:read:admin</code></li>
                </ul>
              </li>
              <li>Click <strong>Activate</strong> your app.</li>
            </ol>

            <h6 class="font-weight-bold text-dark mb-2 mt-4">Step 2: Create Meeting SDK App (For Mobile App)</h6>
            <ol class="pl-3 mb-3">
              <li>In Zoom Marketplace, click <strong>Develop > Build App</strong> and select <strong>Meeting SDK</strong>.</li>
              <li>Give it an App Name (e.g., <em>Neoskool Parent App Live</em>).</li>
              <li>Copy the <strong>SDK Key</strong> (Client ID) and <strong>SDK Secret</strong> (Client Secret).</li>
              <li>Paste them in the form on the left and click <strong>Save & Verify</strong>.</li>
            </ol>

            <a-alert type="info" class="mt-3">
              <div slot="message" class="font-size-12">
                <strong>Zero Per-Minute Fees:</strong> Unlike standard video calling platforms that charge per minute per student, Zoom Meeting SDK uses your school's host licenses so classes can run with zero extra streaming costs.
              </div>
            </a-alert>
          </div>
        </a-card>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: true,
      saving: false,
      testing: false,
      disconnecting: false,
      status: null,
      form: this.$form.createForm(this),
    }
  },
  mounted() {
    this.fetchStatus()
  },
  methods: {
    formatPlan(plan) {
      const map = {
        pro: 'Zoom Pro',
        business: 'Zoom Business',
        education: 'Zoom Education Campus',
        education_plus: 'Zoom Education Plus',
      }
      return map[plan] || plan || 'Zoom Pro'
    },

    fetchStatus() {
      this.loading = true
      this.$http.get('/api/zoom/settings/status')
        .then(({ data }) => {
          this.status = data.data
          if (this.status) {
            this.$nextTick(() => {
              this.form.setFieldsValue({
                account_id: this.status.account_id || '',
                client_id: this.status.client_id || '',
                sdk_key: this.status.sdk_key || '',
                plan_type: this.status.plan_type || 'pro',
              })
            })
          }
        })
        .catch(err => {
          console.error(err)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to load Zoom settings.',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleSubmit(e) {
      e.preventDefault()
      this.form.validateFields((err, values) => {
        if (!err) {
          this.saving = true
          this.$http.post('/api/zoom/settings', values)
            .then(({ data }) => {
              this.$notification.success({
                message: 'Success',
                description: data.message || 'Zoom settings saved successfully!',
              })
              this.fetchStatus()
            })
            .catch(error => {
              console.error(error)
              const msg = error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : 'Failed to save Zoom settings.'
              this.$notification.error({
                message: 'Error',
                description: msg,
              })
            })
            .finally(() => {
              this.saving = false
            })
        }
      })
    },

    testConnection() {
      this.testing = true
      this.$http.post('/api/zoom/settings/test-connection')
        .then(({ data }) => {
          this.$notification.success({
            message: 'Connection Verified',
            description: data.message || 'Zoom connection verified successfully!',
          })
          this.fetchStatus()
        })
        .catch(error => {
          console.error(error)
          const msg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Zoom connection test failed.'
          this.$notification.error({
            message: 'Connection Failed',
            description: msg,
          })
        })
        .finally(() => {
          this.testing = false
        })
    },

    disconnect() {
      this.disconnecting = true
      this.$http.post('/api/zoom/settings/disconnect')
        .then(({ data }) => {
          this.$notification.success({
            message: 'Disconnected',
            description: data.message || 'Zoom settings disconnected.',
          })
          this.form.resetFields()
          this.fetchStatus()
        })
        .catch(error => {
          console.error(error)
          this.$notification.error({
            message: 'Error',
            description: 'Failed to disconnect Zoom.',
          })
        })
        .finally(() => {
          this.disconnecting = false
        })
    },
  },
}
</script>
